"use server";

import type { Prisma } from "@prisma/client";
import { z } from "zod";
import { usersWithPermission } from "@/lib/access";
import { can } from "@/lib/access-rules";
import { defineAction } from "@/lib/define-action";
import { bestEffort } from "@/lib/errors";
import { actionKey } from "@/lib/notification-action-kinds";
import { resolveAction } from "@/lib/notification-actions";
import { notify } from "@/lib/notify";
import { prisma } from "@/lib/prisma";
import { setClientArchived } from "@/lib/clients/archive";
import { getSetting } from "@/lib/settings";
import { creatorGuard } from "./creator-guard";
import { approveSchema, createProjectSchema, rejectSchema, type CreateProjectInput } from "./create-schema";
import { planProject } from "./plan";
import { kickProvisioning, queueProvision } from "./provision";
import { parseDay } from "./schedule";
import { loadTemplateDoc } from "./template-store";

const fail = (error: string) => ({ ok: false as const, error });
const CLOSED = /^(archiv|clos|abgeschlossen)/i;

async function notifyAssignees(projectId: number, exceptUserId: number) {
  const project = await prisma.hubProject.findUnique({
    where: { id: projectId },
    select: { name: true, cards: { select: { assignees: { select: { userId: true } } } } },
  });
  if (!project) return;
  const counts = new Map<number, number>();
  for (const card of project.cards) for (const a of card.assignees) counts.set(a.userId, (counts.get(a.userId) ?? 0) + 1);
  counts.delete(exceptUserId);
  const users = await prisma.user.findMany({ where: { id: { in: [...counts.keys()] }, status: "ACTIVE", deletedAt: null, blockedAt: null }, select: { id: true, email: true, name: true } });
  for (const u of users) {
    const n = counts.get(u.id)!;
    await notify({
      type: "project_card_assigned",
      category: "projects",
      recipients: [{ id: u.id, email: u.email, name: u.name ?? u.email }],
      message: `You have ${n} ${n === 1 ? "task" : "tasks"} on the new project "${project.name}".`,
      link: `/projects/${projectId}`,
    });
  }
}

export const createProject = defineAction({
  guard: creatorGuard,
  safe: "Could not create the project.",
  input: createProjectSchema,
  run: async (ctx, input: CreateProjectInput) => {
    const { userId, access, direct } = ctx;
    const start = parseDay(input.startDate)!;

    const client = await prisma.client.findFirst({ where: { id: input.clientId, deletedAt: null }, select: { id: true, name: true, archivedAt: true } });
    if (!client) return fail("This client no longer exists.");
    // An archived client gets a new project → it is active again, in the Hub and in Bexio (owner, 2026-11-02).
    if (client.archivedAt) await bestEffort(setClientArchived(client.id, false, userId), "reactivate the archived client");

    let existingPackage: { id: number; name: string } | null = null;
    if (input.workPackageId) {
      const wp = await prisma.workPackage.findUnique({
        where: { id: input.workPackageId },
        select: {
          id: true,
          name: true,
          archivedAt: true,
          bexioProject: { select: { clientId: true, status: true } },
        },
      });
      if (!wp || wp.archivedAt !== null || CLOSED.test(wp.bexioProject?.status ?? "") || wp.bexioProject?.clientId !== client.id) {
        return fail("Choose one of this client's open Bexio budgets.");
      }
      existingPackage = { id: wp.id, name: wp.name };
    }

    const moneyOn = (await getSetting("pm.money_enabled")) === "true" && can(access, "pm:money_view");
    if (input.hourlyRate !== null && !can(access, "pm:money_view")) return fail("Only admins and general managers can set rates.");
    if (input.budgetChf !== null && !moneyOn) return fail("Only PM admins can set a CHF budget, and only when money is switched on in the PM settings.");

    const template = input.templateId ? await loadTemplateDoc(input.templateId) : null;
    if (input.templateId && !template) return fail("This template no longer exists. Choose another one.");

    const roleIds = [...new Set(input.roles.map((r) => r.roleLabelId))];
    if (roleIds.length !== input.roles.length) return fail("A role is mapped twice.");
    const labels = roleIds.length ? await prisma.roleLabel.findMany({ where: { id: { in: roleIds } }, select: { id: true, name: true, side: true } }) : [];
    if (labels.length !== roleIds.length) return fail("A role no longer exists. Reload the page and try again.");
    for (const r of input.roles) {
      const label = labels.find((l) => l.id === r.roleLabelId)!;
      if (label.side === "CLIENT" && r.userIds.length) return fail(`${label.name} is filled by the client. Enter names instead of eVia people.`);
      if (label.side === "EVIA" && r.customers.length) return fail(`${label.name} is filled by eVia staff. Choose people from the list instead of typing names.`);
    }

    const userIds = [...new Set(input.roles.flatMap((r) => r.userIds))];
    const people = userIds.length
      ? await prisma.user.findMany({ where: { id: { in: userIds }, kind: "MEMBER", status: "ACTIVE", deletedAt: null, blockedAt: null }, select: { id: true } })
      : [];
    if (people.length !== userIds.length) return fail("One of the people you chose is not an active eVia user.");

    const plan = planProject(template ? { id: template.id, name: template.doc.name, buckets: template.doc.buckets } : null, {
      startDate: input.startDate,
      goLiveDate: input.goLiveDate,
      bucketDates: input.bucketDates,
      budgetHours: input.budgetHours,
      roles: input.roles.map((r) => ({ ...r, customerNames: r.customers.map((c) => c.name) })),
    });

    const linkedArticles = template ? await prisma.article.findMany({ where: { id: { in: template.doc.buckets.flatMap((b) => b.cards.flatMap((c) => c.articleIds)) }, deletedAt: null }, select: { id: true } }) : [];
    const articleOk = new Set(linkedArticles.map((a) => a.id));

    const project = await prisma.$transaction(
      async (tx) => {
        const p = await tx.hubProject.create({
          data: {
            name: input.name,
            clientId: client.id,
            templateId: template?.id ?? null,
            status: direct ? "ACTIVE" : "DRAFT",
            startDate: start,
            goLiveDate: input.goLiveDate ? parseDay(input.goLiveDate) : null,
            packageName: existingPackage ? existingPackage.name : input.packageName,
            workPackageId: existingPackage ? existingPackage.id : null,
            budgetHours: input.budgetHours,
            budgetChf: input.budgetChf,
            hourlyRate: input.hourlyRate,
            ownerId: userId,
          },
          select: { id: true },
        });

        for (const r of input.roles) {
          const rows = [
            ...r.userIds.map((id) => ({ projectId: p.id, roleLabelId: r.roleLabelId, userId: id })),
            ...r.customers.map((c) => ({ projectId: p.id, roleLabelId: r.roleLabelId, customerName: c.name, customerEmail: c.email || null })),
          ];
          if (rows.length) await tx.projectRoleAssignment.createMany({ data: rows });
        }

        for (const [bi, b] of (template?.doc.buckets ?? []).entries()) {
          const pb = plan.buckets[bi];
          const bucket = await tx.projectBucket.create({
            data: { projectId: p.id, name: b.name, position: bi, startDate: pb.start, endDate: pb.end },
            select: { id: true },
          });
          for (const [ci, c] of b.cards.entries()) {
            const pc = pb.cards[ci];
            await tx.projectCard.create({
              data: {
                projectId: p.id,
                bucketId: bucket.id,
                position: ci,
                title: c.title,
                description: c.description || null,
                effortHours: c.effortHours,
                startDate: pc.start,
                dueDate: pc.due,
                assets: c.assets.length ? (c.assets as unknown as Prisma.InputJsonValue) : undefined,
                deliverableKind: c.deliverable?.kind,
                deliverableName: c.deliverable?.name,
                deliverableRoleLabelId: c.deliverable?.kind === "SIGNOFF" ? c.deliverable.roleLabelId : null,
                checklist: { create: c.checklist.map((title, position) => ({ title, position })) },
                roles: { create: [...new Set(c.roleLabelIds)].map((roleLabelId) => ({ roleLabelId })) },
                assignees: { create: pc.userIds.map((userId) => ({ userId })) },
                articles: { create: [...new Set(c.articleIds)].filter((id) => articleOk.has(id)).map((articleId) => ({ articleId })) },
              },
            });
          }
        }

        if (direct && !existingPackage) await queueProvision(tx, p.id);
        return p;
      },
      { timeout: 60_000 },
    );

    if (direct) {
      if (!existingPackage) kickProvisioning();
      await bestEffort(notifyAssignees(project.id, userId), "notify the assignees");
    } else {
      const approvers = await usersWithPermission("projects:approve", { excludeUserId: userId });
      const budgetDesc = existingPackage
        ? `on the existing budget "${existingPackage.name}"`
        : `package "${input.packageName}"`;
      await bestEffort(
        notify({
          type: "project_approval_requested",
          category: "projects",
          recipients: approvers,
          message: `Approve "${input.name}"? Client ${client.name}, ${budgetDesc}, ${input.budgetHours} h${template ? `, template ${template.doc.name}` : ", no template"}.`,
          link: `/projects/${project.id}`,
          actionKey: actionKey("project_approval", project.id),
        }),
        "ask the PM admins for approval",
      );
    }
    return { ok: true as const, id: project.id, status: direct ? ("ACTIVE" as const) : ("DRAFT" as const) };
  },
  paths: ["/projects"],
});

export const approveProject = defineAction({
  guard: "projects:approve",
  safe: "Could not approve the project.",
  input: approveSchema,
  run: async ({ userId }, { id }: { id: number }) => {
    const done = await prisma.hubProject.updateMany({
      where: { id, status: "DRAFT", rejectedAt: null, deletedAt: null },
      data: { status: "ACTIVE", approvedBy: userId, approvedAt: new Date() },
    });
    if (done.count === 0) return fail("This project is no longer waiting for approval.");
    const project = await prisma.hubProject.findUnique({ where: { id }, select: { name: true, ownerId: true, workPackageId: true } });
    if (!project?.workPackageId) {
      await queueProvision(prisma, id);
      kickProvisioning();
    }
    await resolveAction(actionKey("project_approval", id), userId);

    const owner = project && (await prisma.user.findUnique({ where: { id: project.ownerId }, select: { id: true, email: true, name: true } }));
    if (project && owner) {
      await bestEffort(
        notify({
          type: "project_approved",
          category: "projects",
          recipients: [{ id: owner.id, email: owner.email, name: owner.name ?? owner.email }],
          message: `"${project.name}" was approved. The Bexio work package is being created.`,
          link: `/projects/${id}`,
        }),
        "tell the manager about the approval",
      );
    }
    await bestEffort(notifyAssignees(id, userId), "notify the assignees");
    return { ok: true as const };
  },
  paths: (_o, input: { id: number }) => ["/projects", `/projects/${input.id}`],
});

export const rejectProject = defineAction({
  guard: "projects:approve",
  safe: "Could not reject the project.",
  input: rejectSchema,
  run: async ({ userId }, { id, reason }: { id: number; reason: string }) => {
    const done = await prisma.hubProject.updateMany({
      where: { id, status: "DRAFT", rejectedAt: null, deletedAt: null },
      data: { rejectedBy: userId, rejectedAt: new Date(), rejectionReason: reason },
    });
    if (done.count === 0) return fail("This project is no longer waiting for approval.");
    await resolveAction(actionKey("project_approval", id), userId);

    const project = await prisma.hubProject.findUnique({ where: { id }, select: { name: true, ownerId: true } });
    const owner = project && (await prisma.user.findUnique({ where: { id: project.ownerId }, select: { id: true, email: true, name: true } }));
    if (project && owner) {
      await bestEffort(
        notify({
          type: "project_rejected",
          category: "projects",
          recipients: [{ id: owner.id, email: owner.email, name: owner.name ?? owner.email }],
          message: `"${project.name}" was rejected: ${reason}`.slice(0, 500),
          link: `/projects/${id}`,
        }),
        "tell the manager about the rejection",
      );
    }
    return { ok: true as const };
  },
  paths: (_o, input: { id: number }) => ["/projects", `/projects/${input.id}`],
});

export const retryProvision = defineAction({
  guard: "projects:approve",
  safe: "Could not retry.",
  input: z.object({ id: z.number().int().positive() }),
  run: async (_ctx, { id }: { id: number }) => {
    const done = await prisma.bexioProvision.updateMany({
      where: { hubProjectId: id, status: "NEEDS_ATTENTION" },
      data: { status: "QUEUED", attempt: 1, nextRetryAt: new Date(), error: null },
    });
    if (done.count === 0) return fail("Nothing to retry for this project.");
    kickProvisioning();
    return { ok: true as const };
  },
  paths: (_o, input: { id: number }) => [`/projects/${input.id}`],
});
